import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";

import { PageHeader } from "../components/PageHeader";
import { ReturnChip } from "../components/ReturnChip";
import { StepShell } from "../components/StepShell";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import {
  METRIC_BANDS,
  VINTAGE_COLORS,
  useSeasoning,
  type VintageSeasoning,
} from "../lib/api/seasoning";
import { type Segment } from "../lib/api/types";
import {
  useEfficacyVintage,
  type EfficacyVintagePoint,
} from "../lib/api/efficacy";
import { useModelRegistry } from "../lib/api/registry";

const DEFAULT_VINTAGES = ["2024", "2025", "2026"];
// A month-cohort is judgeable once it has ≥ 24 months on book, independent of
// whether its vintage-year as a whole is judgeable.
const MONTHLY_MATURITY_MONTHS = 24;
type MetricKey = "auc" | "ks" | "brier";
type CalibrationMode = "annual" | "monthly";

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function fmtMonth(m: string): string {
  const [y, mo] = m.split("-");
  return `${MONTH_NAMES[parseInt(mo, 10) - 1]} ${y}`;
}

function vintageColor(v: string) {
  return VINTAGE_COLORS[v] ?? "#fb923c";
}

function VintageToggle({
  vintages,
  selected,
  onToggle,
}: {
  vintages: VintageSeasoning[];
  selected: string[];
  onToggle: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {vintages.map((v) => {
        const on = selected.includes(v.vintage);
        const color = vintageColor(v.vintage);
        return (
          <button
            key={v.vintage}
            onClick={() => onToggle(v.vintage)}
            className="rounded-md border px-2.5 py-1 text-xs font-medium transition-colors"
            style={
              on
                ? {
                    backgroundColor: color,
                    borderColor: "transparent",
                    color: "#0b0f1a",
                  }
                : {
                    borderColor: "hsl(var(--border))",
                    color: "hsl(var(--muted-foreground))",
                  }
            }
          >
            {v.vintage}
            {!v.judgeable && (
              <span className="ml-1 text-[10px] opacity-80">· seasoning</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function SeasoningSection({ modelId }: { modelId: number }) {
  const [loanType, setLoanType] = useState<Segment>("OVERALL");
  const { data: seasoningData, isLoading } = useSeasoning(loanType, modelId);
  const vintages = seasoningData?.vintages ?? [];
  const [selected, setSelected] = useState<string[]>(DEFAULT_VINTAGES);

  const toggle = (v: string) =>
    setSelected((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));

  const chartData = useMemo(() => {
    const mobs = [0, 6, 12, 18, 24, 30, 36, 42, 48, 54, 60];
    return mobs.map((mob) => {
      const row: Record<string, number | null | string> = { mob };
      for (const v of vintages) {
        if (!selected.includes(v.vintage)) continue;
        const pt = v.curve.find((c) => c.mob === mob);
        if (!pt) continue;
        row[`${v.vintage}_p`] = pt.nLoans > 0
          ? +((pt.nDefaultsPredicted / pt.nLoans) * 100).toFixed(3)
          : null;
        row[`${v.vintage}_a`] = pt.actual !== null && pt.nLoans > 0
          ? +((pt.nDefaults / pt.nLoans) * 100).toFixed(3)
          : null;
      }
      return row;
    });
  }, [vintages, selected]);

  const selectedVintages = vintages.filter((v) => selected.includes(v.vintage));

  return (
    <Card className="bg-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base font-semibold">
              Did the predictions come true?
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Cumulative predicted vs actual default rate as each vintage ages.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Loan Type
              </span>
              <Select value={loanType} onValueChange={(v) => setLoanType(v as Segment)}>
                <SelectTrigger className="h-8 w-[120px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="OVERALL">Overall</SelectItem>
                  <SelectItem value="PG">PG</SelectItem>
                  <SelectItem value="CORP">Corp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <VintageToggle
              vintages={vintages}
              selected={selected}
              onToggle={toggle}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[320px] animate-pulse rounded-md bg-muted/20" />
        ) : (<>
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                strokeOpacity={0.4}
              />
              <XAxis
                dataKey="mob"
                type="number"
                domain={[0, 60]}
                ticks={[0, 6, 12, 18, 24, 30, 36, 42, 48, 54, 60]}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={{ stroke: "hsl(var(--border))" }}
                label={{
                  value: "Months on book",
                  position: "insideBottom",
                  offset: -4,
                  style: { fontSize: 11, fill: "hsl(var(--muted-foreground))" },
                }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={{ stroke: "hsl(var(--border))" }}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 6,
                  fontSize: 11,
                  color: "hsl(var(--foreground))",
                }}
                labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                itemStyle={{ color: "hsl(var(--foreground))" }}
                formatter={(v, name) => {
                  const n = typeof v === "number" ? v : null;
                  return n === null
                    ? ["—", name as string]
                    : [`${n.toFixed(2)}%`, name as string];
                }}
              />
              {selectedVintages.flatMap((v) => {
                const color = vintageColor(v.vintage);
                return [
                  <Line
                    key={`${v.vintage}_p`}
                    type="monotone"
                    dataKey={`${v.vintage}_p`}
                    name={`${v.vintage} predicted`}
                    stroke={color}
                    strokeDasharray="5 4"
                    strokeWidth={1.75}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />,
                  <Line
                    key={`${v.vintage}_a`}
                    type="monotone"
                    dataKey={`${v.vintage}_a`}
                    name={`${v.vintage} actual`}
                    stroke={color}
                    strokeWidth={2.25}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />,
                ];
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
          {selectedVintages.map((v) => (
            <span
              key={v.vintage}
              className="inline-flex items-center gap-1.5 text-foreground/90"
            >
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: vintageColor(v.vintage) }}
              />
              {v.vintage}
            </span>
          ))}
          <span className="ml-2 text-muted-foreground">
            dashed = predicted · solid = actual
          </span>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          On mature vintages the model is somewhat conservative predicting
          lower default than occurred (e.g. 2022: 2.8% predicted vs 6.1%
          actual). Vintage 2024 is still seasoning and its curve stops early.
        </p>
        </>)}
      </CardContent>
    </Card>
  );
}

function CalibrationSection({ modelId }: { modelId: number }) {
  const [loanType, setLoanType] = useState<Segment>("OVERALL");
  const { data, isLoading } = useSeasoning(loanType, modelId);
  const vintages = data?.vintages ?? [];
  const [mode, setMode] = useState<CalibrationMode>("annual");

  const judgeable = vintages.filter((v) => v.judgeable);
  const nonJudgeable = vintages.filter((v) => !v.judgeable);

  const allVals = vintages
    .flatMap((v) => [
      v.predictedLifetime,
      v.actualToDate,
      ...(v.monthlyPoints ?? []).flatMap((m) => [m.predicted, m.actual]),
    ])
    .filter((n): n is number => n != null && !isNaN(n));

  const rawMin = allVals.length ? Math.min(...allVals) : 0;
  const rawMax = allVals.length ? Math.max(...allVals) : 7;
  const pad = Math.max((rawMax - rawMin) * 0.12, 0.3);
  const axisMin = parseFloat(Math.max(0, rawMin - pad).toFixed(2));
  const axisMax = parseFloat((rawMax + pad).toFixed(2));

  return (
    <Card className="bg-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base font-semibold">
              Where do the vintages land?
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Predicted vs actual lifetime default rate, per vintage. Above the
              line = the model under-predicted risk.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Loan Type
              </span>
              <Select value={loanType} onValueChange={(v) => setLoanType(v as Segment)}>
                <SelectTrigger className="h-8 w-[120px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="OVERALL">Overall</SelectItem>
                  <SelectItem value="PG">PG</SelectItem>
                  <SelectItem value="CORP">Corp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1 rounded-md border border-border bg-card p-0.5">
              {(["annual", "monthly"] as CalibrationMode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`rounded px-2.5 py-1 text-xs font-medium capitalize transition-colors ${
                    mode === m
                      ? "bg-amber text-background"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[320px] animate-pulse rounded-md bg-muted/20" />
        ) : (<>
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 8, right: 16, left: 0, bottom: 12 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                strokeOpacity={0.4}
              />
              <XAxis
                type="number"
                dataKey="x"
                name="Predicted"
                domain={[axisMin, axisMax]}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={{ stroke: "hsl(var(--border))" }}
                tickFormatter={(v) => `${v}%`}
                label={{
                  value: "Predicted lifetime default %",
                  position: "insideBottom",
                  offset: -4,
                  style: { fontSize: 11, fill: "hsl(var(--muted-foreground))" },
                }}
              />
              <YAxis
                type="number"
                dataKey="y"
                name="Actual"
                domain={[axisMin, axisMax]}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={{ stroke: "hsl(var(--border))" }}
                tickFormatter={(v) => `${v}%`}
              />
              <ZAxis type="number" dataKey="z" range={[60, 200]} />
              <ReferenceLine
                segment={[
                  { x: axisMin, y: axisMin },
                  { x: axisMax, y: axisMax },
                ]}
                stroke="var(--amber)"
                strokeDasharray="4 4"
                strokeOpacity={0.7}
                label={{
                  value: "perfect calibration",
                  position: "insideTopRight",
                  fill: "var(--amber)",
                  fontSize: 10,
                }}
              />
              <Tooltip
                cursor={{ strokeDasharray: "3 3", stroke: "hsl(var(--border))" }}
                content={({ payload }) => {
                  if (!payload || !payload.length) return null;
                  const p = payload[0].payload as {
                    vintage: string;
                    x: number;
                    y: number;
                    judgeable: boolean;
                    label?: string;
                  };
                  return (
                    <div className="rounded-md border border-border bg-popover p-2 text-[11px]">
                      <div className="font-semibold text-foreground">
                        {p.label ?? `Vintage ${p.vintage}`}
                        {!p.judgeable && (
                          <span className="ml-2 text-amber">
                            still seasoning
                          </span>
                        )}
                      </div>
                      <div className="text-muted-foreground">
                        Predicted {p.x.toFixed(2)}% · Actual {p.y.toFixed(2)}%
                      </div>
                    </div>
                  );
                }}
              />

              {mode === "annual" &&
                judgeable.map((v) => (
                  <Scatter
                    key={v.vintage}
                    name={v.vintage}
                    data={[
                      {
                        vintage: v.vintage,
                        x: v.predictedLifetime,
                        y: v.actualToDate,
                        z: 200,
                        judgeable: true,
                      },
                    ]}
                    fill={vintageColor(v.vintage)}
                  />
                ))}

              {mode === "monthly" &&
                vintages.map((v) => {
                  // Per-cohort gate: show only month-cohorts with ≥ 24 MOB,
                  // even when the vintage-year isn't judgeable yet.
                  const mature = (v.monthlyPoints ?? []).filter(
                    (m) => m.mob >= MONTHLY_MATURITY_MONTHS,
                  );
                  if (mature.length === 0) return null;
                  return (
                    <Scatter
                      key={v.vintage}
                      name={v.vintage}
                      data={mature.map((m) => ({
                        vintage: v.vintage,
                        x: m.predicted,
                        y: m.actual,
                        z: 80,
                        judgeable: true,
                        label: `${m.month} ${v.vintage} · ${m.mob}mo`,
                      }))}
                      fill={vintageColor(v.vintage)}
                      fillOpacity={0.75}
                    />
                  );
                })}

              {mode === "annual" && (
                <Scatter
                  name="seasoning"
                  data={nonJudgeable.map((v) => ({
                    vintage: v.vintage,
                    x: v.predictedLifetime,
                    y: v.actualToDate,
                    z: 140,
                    judgeable: false,
                  }))}
                  fill={vintageColor("2024")}
                  fillOpacity={0.35}
                  stroke={vintageColor("2024")}
                  strokeWidth={1.5}
                />
              )}
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
          {judgeable.map((v) => (
            <span
              key={v.vintage}
              className="inline-flex items-center gap-1.5 text-foreground/90"
            >
              <span
                className="inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: vintageColor(v.vintage) }}
              />
              {v.vintage}
            </span>
          ))}
          {nonJudgeable.map((v) => (
            <span
              key={v.vintage}
              className="inline-flex items-center gap-1.5 text-muted-foreground"
            >
              <span
                className="inline-block h-2.5 w-2.5 rounded-full border"
                style={{
                  borderColor: vintageColor(v.vintage),
                  backgroundColor: "transparent",
                }}
              />
              {v.vintage} · seasoning
            </span>
          ))}
        </div>

        <p className="mt-3 text-sm text-muted-foreground">
          Above the line = actual defaults exceeded the model's prediction.
          {mode === "monthly"
            ? " Monthly view shows how tightly each vintage's month-cohorts cluster around its annual point."
            : " Recent vintages sit low only because their losses have not yet emerged tagged as still seasoning."}
        </p>
        </>)}
      </CardContent>
    </Card>
  );
}

function avg(vals: (number | null)[]): number | null {
  const nums = vals.filter((n): n is number => n !== null);
  return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null;
}

const DISC_PALETTE = [
  "#22d3ee", "#60a5fa", "#a78bfa", "#f472b6",
  "#fb923c", "#facc15", "#4ade80", "#f87171",
  "#34d399", "#818cf8", "#e879f9", "#38bdf8",
];

function discYearColor(yr: string, sortedYears: string[]): string {
  const idx = sortedYears.indexOf(yr);
  return DISC_PALETTE[idx % DISC_PALETTE.length];
}

function DiscriminationSection() {
  const { data: vintageData, isLoading } = useEfficacyVintage();
  const [loanType, setLoanType] = useState<string>("OVERALL");
  const [status, setStatus] = useState<string>("delinquency");
  const [metric, setMetric] = useState<MetricKey>("auc");
  const [selectedYears, setSelectedYears] = useState<string[]>([]);
  const { data: seasoningData } = useSeasoning(loanType as Segment, 1);
  const judgeableByVintage = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const v of seasoningData?.vintages ?? []) map[v.vintage] = v.judgeable;
    return map;
  }, [seasoningData]);

  // Filter points by selected segment + status
  const points = useMemo(
    () =>
      (vintageData?.points ?? []).filter(
        (p) => p.segment === loanType && p.metricName === status,
      ),
    [vintageData, loanType, status],
  );

  // All unique years in filtered data
  const years = useMemo(() => {
    const s = new Set(points.map((p) => String(p.year)));
    return Array.from(s).sort();
  }, [points]);

  // Default the selection to 2024–2026 when available years change
  // (loanType/status switch); fall back to all years if none of those exist.
  useEffect(() => {
    const preferred = ["2024", "2025", "2026"].filter((y) => years.includes(y));
    setSelectedYears(preferred.length ? preferred : years);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years.join(",")]);

  const toggleYear = (yr: string) =>
    setSelectedYears((s) => (s.includes(yr) ? s.filter((x) => x !== yr) : [...s, yr]));

  const activeYears = selectedYears.length ? selectedYears : years;

  // Pivot: one row per month_num, columns are activeYears only
  const chartData = useMemo(() => {
    const byMonthNum: Record<number, Record<string, number | null>> = {};
    for (const p of points) {
      if (!activeYears.includes(String(p.year))) continue;
      const mn = p.monthNum;
      if (!byMonthNum[mn]) byMonthNum[mn] = {};
      byMonthNum[mn][String(p.year)] = p[metric];
    }
    return Object.entries(byMonthNum)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([mn, vals]) => ({ monthNum: Number(mn), ...vals }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, metric, activeYears.join(",")]);

  // Table: one row per year, averages of selected metric.
  // NOTE: independent of the year selection — always shows every available year.
  const tableRows = useMemo(() => {
    const byYear: Record<string, EfficacyVintagePoint[]> = {};
    for (const p of points) {
      const y = String(p.year);
      if (!byYear[y]) byYear[y] = [];
      byYear[y].push(p);
    }
    return Object.entries(byYear)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([year, pts]) => {
        const aucAvg = avg(pts.map((p) => p.auc));
        const maxMob = Math.max(...pts.map((p) => p.monthOnBook));
        return {
          year,
          auc: aucAvg,
          ks: avg(pts.map((p) => p.ks)),
          brier: avg(pts.map((p) => p.brier)),
          maxMob,
          status: aucAvg !== null ? (aucAvg >= METRIC_BANDS.AUC.min ? "PASS" : "WARN") : null,
        };
      });
  }, [points]);

  const band =
    metric === "auc" ? METRIC_BANDS.AUC : metric === "ks" ? METRIC_BANDS.KS : METRIC_BANDS.Brier;
  const yDomain: [number, number] = band.domain;
  const threshold = "min" in band ? band.min : band.max;
  const higherBetter = band.betterIsHigher;
  const greenAbove = band.greenAbove;

  const caption =
    metric === "auc"
      ? "Lines above the green line clear the minimum discrimination standard (AUC ≥ 0.65)."
      : metric === "ks"
        ? "Lines above the green line clear the minimum separation standard (KS ≥ 0.25)."
        : "Lines below the green line are within acceptable calibration error (Brier ≤ 0.15) — lower is better.";

  type DiscTableRow = { year: string; auc: number | null; ks: number | null; brier: number | null; maxMob: number; status: string | null };

  const tableCols: ColumnDef<DiscTableRow>[] = [
    {
      id: "year",
      header: "Year",
      cell: ({ row }) => {
        const seasoning = judgeableByVintage[row.original.year] === false;
        return (
          <span className={`inline-flex items-center gap-2 font-medium ${seasoning ? "text-amber" : "text-foreground"}`}>
            <span
              className="inline-block h-[2px] w-4 rounded"
              style={{ backgroundColor: discYearColor(row.original.year, years) }}
            />
            {row.original.year}
            {seasoning && (
              <span className="ml-1 text-[10px] opacity-80">· seasoning</span>
            )}
          </span>
        );
      },
    },
    {
      id: "auc",
      header: "AUC (avg)",
      cell: ({ row }) =>
        row.original.auc === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="tabular-nums">{row.original.auc.toFixed(4)}</span>
        ),
    },
    {
      id: "ks",
      header: "KS (avg)",
      cell: ({ row }) =>
        row.original.ks === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="tabular-nums">{row.original.ks.toFixed(4)}</span>
        ),
    },
    {
      id: "brier",
      header: "Brier (avg)",
      cell: ({ row }) =>
        row.original.brier === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="tabular-nums">{row.original.brier.toFixed(4)}</span>
        ),
    },
    {
      id: "maxMob",
      header: "Max MOB",
      cell: ({ row }) => (
        <span className="tabular-nums text-muted-foreground">{row.original.maxMob}</span>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => {
        const { status: s, maxMob, year } = row.original;
        // Status is only shown for 2024 onwards; blank for 2018–2023.
        if (Number(year) <= 2023) {
          return null;
        }
        if (!s || maxMob < 12) {
          return (
            <span className="inline-flex items-center gap-1 rounded-md border border-amber/40 bg-amber-dim px-2 py-0.5 text-[10px] font-semibold uppercase text-amber">
              Seasoning
            </span>
          );
        }
        return s === "PASS" ? (
          <span className="inline-flex items-center gap-1 rounded-md border border-[color:var(--approve)]/40 bg-[color:var(--approve)]/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-[color:var(--approve)]">
            Judgeable
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-destructive">
            Alert
          </span>
        );
      },
    },
  ];

  const table = useReactTable({
    data: tableRows,
    columns: tableCols,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <Card className="bg-card">
        <CardContent>
          <div className="mt-4 h-64 animate-pulse rounded-md bg-muted/20" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base font-semibold">
              Model accuracy, where it can be measured.
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Efficacy by vintage each line is one origination year, plotted
              by months on book. Source:{" "}
              <code className="text-[10px]">model_observability</code>.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Loan Type
            </span>
            <Select value={loanType} onValueChange={setLoanType}>
              <SelectTrigger className="h-8 w-[110px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="OVERALL">Overall</SelectItem>
                <SelectItem value="PG">PG</SelectItem>
                <SelectItem value="CORP">Corp</SelectItem>
              </SelectContent>
            </Select>

            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Status
            </span>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-8 w-[130px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="delinquency">Delinquency</SelectItem>
                <SelectItem value="default">Default</SelectItem>
              </SelectContent>
            </Select>

            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Metric
            </span>
            <Select value={metric} onValueChange={(v) => setMetric(v as MetricKey)}>
              <SelectTrigger className="h-8 w-[90px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auc">AUC</SelectItem>
                <SelectItem value="ks">KS</SelectItem>
                <SelectItem value="brier">Brier</SelectItem>
              </SelectContent>
            </Select>

            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Year
            </span>
            <div className="flex flex-wrap items-center gap-1">
              {years.map((yr) => {
                const on = activeYears.includes(yr);
                const color = discYearColor(yr, years);
                return (
                  <button
                    key={yr}
                    onClick={() => toggleYear(yr)}
                    className="rounded-md border px-2 py-1 text-xs font-medium transition-colors"
                    style={
                      on
                        ? { backgroundColor: color, borderColor: "transparent", color: "#0b0f1a" }
                        : { borderColor: "hsl(var(--border))", color: "hsl(var(--muted-foreground))" }
                    }
                  >
                    {yr}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 8, right: 16, left: 0, bottom: 16 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                strokeOpacity={0.4}
              />
              <XAxis
                dataKey="monthNum"
                type="number"
                domain={[1, 12]}
                ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
                tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={{ stroke: "hsl(var(--border))" }}
                label={{
                  value: "Month",
                  position: "insideBottom",
                  offset: -8,
                  style: { fontSize: 11, fill: "hsl(var(--muted-foreground))" },
                }}
              />
              <YAxis
                domain={yDomain}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickLine={false}
                axisLine={{ stroke: "hsl(var(--border))" }}
              />

              {greenAbove ? (
                <>
                  <ReferenceArea y1={threshold} y2={yDomain[1]} fill="var(--approve)" fillOpacity={0.08} stroke="none" />
                  <ReferenceArea y1={yDomain[0]} y2={threshold} fill="var(--destructive)" fillOpacity={0.08} stroke="none" />
                </>
              ) : (
                <>
                  <ReferenceArea y1={yDomain[0]} y2={threshold} fill="var(--approve)" fillOpacity={0.08} stroke="none" />
                  <ReferenceArea y1={threshold} y2={yDomain[1]} fill="var(--destructive)" fillOpacity={0.08} stroke="none" />
                </>
              )}
              <ReferenceLine
                y={threshold}
                stroke="var(--approve)"
                strokeDasharray="4 4"
                strokeOpacity={0.85}
                label={{
                  value: higherBetter ? `min ${threshold}` : `max ${threshold}`,
                  position: "insideTopRight",
                  fill: "var(--approve)",
                  fontSize: 10,
                }}
              />

              <Tooltip
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 6,
                  fontSize: 11,
                  color: "hsl(var(--foreground))",
                }}
                labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                labelFormatter={(v) => `Month ${v}`}
                itemStyle={{ color: "hsl(var(--foreground))" }}
                formatter={(v, name) => {
                  const n = typeof v === "number" ? v : null;
                  return n === null ? ["—", name as string] : [n.toFixed(4), name as string];
                }}
              />

              {activeYears.map((yr) => (
                <Line
                  key={yr}
                  type="monotone"
                  dataKey={yr}
                  name={yr}
                  stroke={discYearColor(yr, years)}
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
          {activeYears.map((yr) => (
            <span key={yr} className="inline-flex items-center gap-1.5 text-foreground/90">
              <span
                className="inline-block h-[2px] w-5"
                style={{ backgroundColor: discYearColor(yr, years) }}
              />
              {yr}
            </span>
          ))}
        </div>

        <p className="text-xs text-muted-foreground">{caption}</p>

        <div className="rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id} className="border-border hover:bg-transparent">
                  {hg.headers.map((h) => (
                    <TableHead key={h.id} className="text-[11px] uppercase tracking-[0.18em] text-amber">
                      {flexRender(h.column.columnDef.header, h.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => {
                const isSeasoning = judgeableByVintage[row.original.year] === false;
                return (
                <TableRow key={row.id} className={`border-border hover:bg-muted/30 ${isSeasoning ? "text-amber" : ""}`}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

export function ObservabilityPage() {
  const [searchParams] = useSearchParams();
  const fromParam = searchParams.get("from");
  const { data: models } = useModelRegistry();
  const [modelId, setModelId] = useState<number>(1);

  // Deduplicate by model_name+version — multiple rows (PG/CORP) become one dropdown entry
  const uniqueModels = models
    ? models.filter(
        (m, i, arr) =>
          arr.findIndex(
            (x) => x.modelName === m.modelName && x.modelVersion === m.modelVersion
          ) === i
      )
    : [];

  return (
    <StepShell step={3} modelId={modelId} models={uniqueModels} onModelChange={setModelId}>
      <PageHeader
        title="Observability"
        subtitle="Did Athena's predictions come true as the loans aged? Vintage seasoning answers honestly judgement requires maturity."
      />

      {fromParam === "health" && (
        <div className="mt-4">
          <ReturnChip />
        </div>
      )}

      <div className="mt-6 space-y-6">
        <SeasoningSection modelId={modelId} />
        <CalibrationSection modelId={modelId} />
        <DiscriminationSection />
      </div>
    </StepShell>
  );
}
